import { Roles } from "@/constants/roles"
import { SPACE_PAID_PLANS } from "@/constants/spaces"
import { env } from "@/env"
import type { Space, Subscriptions } from "@/types"

export function getUserType(roles: Space["roles"]) {
  if (roles.length === 0) return "admin" as const
  // Cast to our Roles keys: the external SidebarSpace["roles"] union predates the viewer role.
  switch (roles[0] as keyof typeof Roles) {
    case Roles["role:edu-admin"]:
    case Roles["role:pro-admin"]:
      return "admin" as const
    case Roles["role:edu-teacher"]:
      return "teacher" as const
    case Roles["role:edu-student"]:
      return "student" as const
    case Roles["role:pro-editor"]:
      return "editor" as const
    case Roles["role:pro-service"]:
      return "service" as const
    case Roles["role:pro-viewer"]:
      return "viewer" as const
    default:
      return "admin" as const
  }
}

/** The user's role within a space, the finest-grained classification. SSOT for "who am I". */
export type UserType = ReturnType<typeof getUserType>

export function isPlanExpired(space: Space) {
  return space.space === "pro" && !space.plan_id
}

export function getMembersLimit(space: Space, thingsLimit: number): number {
  if (space.plan_seats && space.plan_seats !== 0) return space.plan_seats
  if (space.space === "pro") return thingsLimit === 100 ? 50 : Infinity
  return Infinity
}

export function getPlanUpgradeLink(space: Space, isMinor: boolean, plans: Subscriptions["plans"]) {
  const userType = getUserType(space.roles)

  switch (space.space) {
    case "pro":
      if (userType === "admin" && !plans.some(item => Object.keys(SPACE_PAID_PLANS).includes(item))) {
        return `${env.VITE_STORE}/enterprise/purchase/plan?frequency=yearly&organization_id=${space.id}`
      }
      break
    case "edu":
      // Edu can always upgrade (i.e. get more seats)
      if (userType === "admin") {
        return `${env.VITE_STORE}/education/purchase?organization_id=${space.id}`
      }
      break
    default:
      if (!isMinor && !plans.includes(SPACE_PAID_PLANS["create-maker-plus"])) {
        return `${env.VITE_STORE}/purchase/plan?plan=maker&frequency=yearly`
      }
      break
  }

  return undefined
}

export function getPlanName(space: Space, spaceRecap: Subscriptions | undefined) {
  if (space.space === "pro" && spaceRecap?.limits.cloud_things !== 100) return "Enterprise"
  const isExpiredPlan = isPlanExpired(space)
  const { isPaidPlan } = getRestrictionsRecap(spaceRecap, undefined, isExpiredPlan)

  return isPaidPlan
    ? SPACE_PAID_PLANS[
        // Find the first match of one (any) paid plan in the spaceRecap.plans array. We have to suppose we'll only have one active paid plan per user/space
        Object.keys(SPACE_PAID_PLANS).filter(plan =>
          spaceRecap?.plans.includes(plan)
        )[0] as keyof typeof SPACE_PAID_PLANS
      ]
    : "Free"
}

/*
 * =============================================================================
 * Restrictions recap
 * =============================================================================
 */

const LORA_DEVICE_IDENTIFIERS = [
  "cloud_devices_arduino_mkrwan1310",
  "cloud_devices_arduino_mkrwan1300",
  "cloud_devices_nonarduino_lora-device",
  "cloud_devices_linux_lora-gateway"
]

/** Plan quota buckets tracked per resource (mirrors the keys of `getRestrictionsRecap`'s `usage`). */
export type ResourceQuota = "things" | "sketches" | "api" | "triggers" | "compilation"

/**
 * Extract all restrictions and usage information from space and user subscriptions.
 * @returns Comprehensive restrictions and usage information
 */
export function getRestrictionsRecap(
  /** Subscription data for the current space */
  spaceRecap: Subscriptions | undefined,
  /** Subscription data for the current user */
  userRecap: Subscriptions | undefined,
  /** Whether the pro plan is expired */
  planExpired: boolean
) {
  /**
   * Check if the current space has a paid plan.
   */
  const isPaidPlan = spaceRecap?.plans.some(item => Object.keys(SPACE_PAID_PLANS).includes(item)) || false

  // Get limits
  const things = spaceRecap?.usage.cloud_things || 0
  const api = spaceRecap?.usage.cloud_api_clients || 0

  /**
   * Get integrations for the current space
   */
  const integrations = {
    hasLoraDevice: spaceRecap
      ? Object.keys(spaceRecap.usage)
          .filter(key => LORA_DEVICE_IDENTIFIERS.includes(key))
          .reduce((acc, key) => (spaceRecap.usage[key] || 0) + acc, 0) > 0
      : false,
    hasFoundries: Boolean(spaceRecap?.limits.foundries),
    hasEdgeImpulse: Boolean(spaceRecap?.limits.edge_impulse)
  }

  /** Usage recap */
  const usage = {
    things: {
      used: things,
      limit: spaceRecap?.limits.cloud_things || 0
    },
    sketches: {
      used: spaceRecap?.usage.create_disk_sketches || 0,
      limit:
        // If disk limit is over 1Gb, it means it's unlimited
        (spaceRecap?.limits.create_sketches_disk || 0) > 1048576000
          ? Infinity
          : spaceRecap?.limits.create_sketches_disk || 0
    },
    compilation: {
      used: spaceRecap?.usage.create_compilation_day || 0,
      limit: spaceRecap?.limits.create_compilation_day || 0
    },
    api: {
      used: api,
      limit: userRecap?.limits.cloud_api ? 1000 : 0
    },
    triggers: {
      used: spaceRecap?.usage.cloud_triggers || 0,
      limit: spaceRecap?.limits.cloud_triggers || 0
    }
  }

  // Quotas should always be positive (meaning we have less than the limit).
  const quotadiff = Object.fromEntries(
    Object.entries(usage).map(([key, value]) => [key, value.limit - value.used])
  ) as Record<keyof typeof usage, number>

  // Evaluate quota state for each limit.
  const isAtQuota = Object.fromEntries(
    Object.entries(quotadiff).map(([key, value]) => [key, planExpired || value <= 0])
  ) as Record<keyof typeof quotadiff, boolean>

  const isOverQuota = Object.fromEntries(
    Object.entries(quotadiff).map(([key, value]) => [key, planExpired || value < 0])
  ) as Record<keyof typeof quotadiff, boolean>

  // Shorthands to check if any of the limits is reached
  const isOverReached = Object.values(isOverQuota).some(Boolean) // ! This indicates generic overquota (usually expired plan)

  // Features that are enabled for the current space
  const features = {
    triggers: Boolean(spaceRecap?.limits.cloud_triggers),
    ota: Boolean(spaceRecap?.limits.ota),
    cloudApi: Boolean(userRecap?.limits.cloud_api)
  }

  return {
    isPaidPlan,
    integrations,
    usage,
    quotadiff,
    isAtQuota,
    isOverQuota,
    isOverReached,
    features
  }
}
